import * as logClient from "@classdojo/log-client";
import { useEffect, useCallback, useRef } from "react";
import FullBleedScreen from "components/BeyondToolkit/FullBleedScreen";
import ToolkitAudioActivityChrome from "components/ToolkitAudioActivityChrome";
import { useAudioPlayer, playStates } from "hooks/useAudioPlayer";
import { openPointsSheet } from "src/pods/webviewInit";
import { logParentToolkitEvent } from "src/utils/logEvent";
import { SHAKE_SILLIES } from "utils/beyondToolkitTools";
import styles from "./index.module.css";
import { useShakeSillies as useShakeSilliesVm } from "./useShakeSillies.vm";

type ShakeSilliesProps = {
  di?: { useVm?: typeof useShakeSilliesVm };
};

export default function ShakeSillies({ di }: ShakeSilliesProps) {
  const useVm = di?.useVm ?? useShakeSilliesVm;
  const { recordUsage } = useVm();

  const shakeSillies = SHAKE_SILLIES();

  useEffect(() => {
    logParentToolkitEvent({ eventName: `${shakeSillies.loggingId}.loaded` });
  }, [shakeSillies.loggingId]);

  const {
    play: playMain,
    pause,
    playState,
    secondsRemaining,
  } = useAudioPlayer({
    src: shakeSillies.mainAudio,
    expectedDuration: shakeSillies.duration,
  });

  const { play: playIntro, pause: pauseIntro } = useAudioPlayer({
    src: "/static/media/activity-wiggleit_start.mp3",
    expectedDuration: 17,
  });

  const { play: playOutro } = useAudioPlayer({
    src: "/static/media/activity-wiggleit_end.mp3",
    expectedDuration: 3,
  });

  // play intro audio clip on mount

  useEffect(() => {
    playIntro();
  }, [playIntro]);

  // pause intro clip when we start the main clip
  const play = useCallback(() => {
    pauseIntro();
    playMain();

    // send usage on first play event, and not on subsequent play events (i.e. after pause)
    recordUsage();
  }, [pauseIntro, playMain, recordUsage]);

  // play outro after the main clip

  useEffect(() => {
    if (playState === playStates.DONE) {
      playOutro();
      logParentToolkitEvent({ eventName: `${shakeSillies.loggingId}.activity_completed` });
    }
  }, [shakeSillies.loggingId, playOutro, playState]);

  const videoNodeRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (videoNodeRef.current) {
      if (playState === playStates.PLAYING) {
        videoNodeRef.current.play().catch(() => {
          logClient.sendMetrics([
            {
              type: "increment",
              value: 1,
              metricName: "toolkit.media.video.play.error",
            },
          ]);
        });
      } else if (playState === playStates.PAUSED) {
        videoNodeRef.current.pause();
      }
    }
  }, [playState]);

  return (
    <FullBleedScreen variant="shakeSillies">
      <ToolkitAudioActivityChrome
        loggingId={shakeSillies.loggingId}
        play={play}
        pause={pause}
        playState={playState}
        secondsRemaining={secondsRemaining}
        closeAndGivePoints={() => {
          openPointsSheet();
          window.history.back();
        }}
        mainContent={
          // eslint-disable-next-line jsx-a11y/media-has-caption -- decorative background video for classroom activity
          <video
            playsInline
            preload="auto"
            ref={(n) => (videoNodeRef.current = n)}
            src={"/static/media/wiggleit.mp4#t=0.1"}
            loop
            className={styles.video}
          />
        }
      />
    </FullBleedScreen>
  );
}
